import { Boxes, RefreshCw, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadOptionalModules, type OptionalModuleItem, type OptionalModuleRuntimeState } from './modules-api';
import './modules.css';

function runtimeTone(state: OptionalModuleRuntimeState): 'good' | 'warning' | 'bad' | 'neutral' {
  if (state === 'online') return 'good';
  if (state === 'starting' || state === 'recovering' || state === 'degraded') return 'warning';
  if (state === 'offline') return 'bad';
  if (state === 'unknown') return 'warning';
  return 'neutral';
}

function friendly(value: string): string {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function lifecycleLabel(module: OptionalModuleItem): string {
  if (module.lifecycle === 'ready') return 'Ready';
  if (module.lifecycle === 'development') return 'Development';
  return 'Reserved';
}

function runtimeLabel(state: OptionalModuleRuntimeState): string {
  if (state === 'not-installed') return 'Not installed';
  if (state === 'unknown') return 'Unknown';
  return friendly(state);
}

function formatStamp(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function ModulesPage() {
  const [modules, setModules] = useState<OptionalModuleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    try {
      setModules(await loadOptionalModules());
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Optional modules could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 10000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const summary = useMemo(() => ({
    ready: modules.filter((module) => module.lifecycle === 'ready').length,
    development: modules.filter((module) => module.lifecycle === 'development').length,
    reserved: modules.filter((module) => module.lifecycle === 'reserved').length,
    online: modules.filter((module) => module.runtimeState === 'online').length,
  }), [modules]);

  if (loading && modules.length === 0) return <div className="ffs-next-loading">Loading Modules…</div>;

  return (
    <div className="ffs-workspace-page ffs-modules-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div>
          <small>OPTIONAL CAPABILITIES</small>
          <h2>Modules</h2>
          <p>Read-only lifecycle and runtime visibility for optional FFSControl feature packages. Module registration does not grant process execution or OBS scene control.</p>
        </div>
        <button type="button" className="ffs-button secondary" onClick={() => void refresh()}><RefreshCw size={15} /> Refresh</button>
      </section>

      <section className="ffs-modules-summary">
        <article><span>Ready</span><strong>{summary.ready.toLocaleString('en-GB')}</strong></article>
        <article><span>Development</span><strong>{summary.development.toLocaleString('en-GB')}</strong></article>
        <article><span>Reserved</span><strong>{summary.reserved.toLocaleString('en-GB')}</strong></article>
        <article><span>Online now</span><strong>{summary.online.toLocaleString('en-GB')}</strong></article>
      </section>

      <section className="ffs-workspace-card">
        <header><div><small>REGISTERED MODULES</small><h3>Lifecycle &amp; runtime</h3></div><Boxes size={19} /></header>
        {modules.length === 0 ? <div className="ffs-empty-state">No optional modules were returned.</div> : (
          <div className="ffs-modules-grid">
            {modules.map((module) => (
              <article key={module.id}>
                <div className="ffs-module-heading">
                  <div><strong>{module.displayName}</strong><span><code>{module.id}</code> · API v{module.apiVersion}</span></div>
                  <span className={'ffs-status ' + runtimeTone(module.runtimeState)}>{runtimeLabel(module.runtimeState).toUpperCase()}</span>
                </div>
                <dl>
                  <div><dt>Lifecycle</dt><dd>{lifecycleLabel(module)}</dd></div>
                  <div><dt>Runtime owner</dt><dd>{friendly(module.runtime)}</dd></div>
                  <div><dt>Connected</dt><dd>{module.connected ? 'Yes' : 'No'}</dd></div>
                </dl>
                <div className="ffs-module-contract">
                  <div><span>Capabilities</span>{module.capabilities.length ? <ul>{module.capabilities.map((item) => <li key={item}><code>{item}</code></li>)}</ul> : <small>None declared</small>}</div>
                  <div><span>Events</span>{module.events.length ? <ul>{module.events.map((item) => <li key={item}><code>{item}</code></li>)}</ul> : <small>None declared</small>}</div>
                </div>
                {module.runtimeDetail?.kind === 'wam' ? (
                  <div className="ffs-module-recovery">
                    <div><span>Managed</span><strong>{module.runtimeDetail.managed ? 'Yes' : 'No'}</strong></div>
                    <div><span>Process</span><strong>{friendly(module.runtimeDetail.processState)}</strong></div>
                    <div><span>Restart attempts</span><strong>{module.runtimeDetail.restartAttempts.toLocaleString('en-GB')}</strong></div>
                    <div><span>Health failures</span><strong>{module.runtimeDetail.consecutiveHealthFailures.toLocaleString('en-GB')}</strong></div>
                    <div><span>Last healthy</span><strong>{formatStamp(module.runtimeDetail.lastHealthyAt)}</strong></div>
                    <div><span>Next retry</span><strong>{formatStamp(module.runtimeDetail.nextRetryAt)}</strong></div>
                    <div className={module.runtimeDetail.terminalFailure ? 'terminal' : ''}><span>Terminal failure</span><strong>{module.runtimeDetail.terminalFailure ? 'Yes' : 'No'}</strong></div>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="ffs-workspace-card ffs-modules-safety">
        <header><div><small>SAFETY BOUNDARY</small><h3>Discovery only</h3></div><ShieldCheck size={19} /></header>
        <p>This workspace has no generic run, start, stop, process, shell or scene-switch action. Future modules must add their own bounded implementation paths and remain isolated from other optional modules.</p>
      </section>
    </div>
  );
}
